import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
type Consultancy = {
  title: string;
  editionLabel: string;
  emoji: string;
};
const CONSULTANCIES: Consultancy[] = [{
  title: 'Consultoría Web Estratégica',
  editionLabel: 'Sesión 1:1 con Nacho',
  emoji: '🚀'
}, {
  title: 'Diagnóstico de Página Web',
  editionLabel: 'Revisión Estratégica',
  emoji: '🔍'
}, {
  title: 'Llamada Estratégica',
  editionLabel: 'Plan de Acción Personalizado',
  emoji: '📞'
}, {
  title: 'Cotización de Página Web',
  editionLabel: 'Proyecto a Medida',
  emoji: '💼'
}, {
  title: 'Auditoría de Conversión',
  editionLabel: 'Análisis Completo',
  emoji: '📈'
}];
const CHILEAN_NAMES: string[] = ['Matías Rojas', 'Sofía Muñoz', 'Diego Fuentes', 'Camila Vergara', 'Valentina Soto', 'Benjamín Araya', 'Catalina Pérez', 'Joaquín Castro', 'Antonia Reyes', 'Sebastián Morales', 'Florencia Bravo', 'Tomás Espinoza', 'Constanza Herrera', 'Vicente Salazar', 'Isidora Cáceres', 'Maximiliano Tapia', 'Javiera Contreras', 'Agustín Silva', 'Martina Núñez', 'Cristóbal Vargas', 'Fernanda Ahumada', 'Felipe Henríquez', 'Trinidad Carrasco', 'Lucas Pinto', 'Emilia Cortés', 'Nicolás Sandoval', 'Amanda Riquelme', 'Ignacio Lagos', 'Renata Figueroa', 'Mateo Saavedra', 'Francisca Vidal', 'Bastián Olivares', 'Josefa Donoso', 'Gabriel Mansilla', 'Magdalena Quiroz'];
const CHILEAN_CITIES: string[] = ['Santiago', 'Valparaíso', 'Viña del Mar', 'Concepción', 'La Serena', 'Antofagasta', 'Temuco', 'Rancagua', 'Puerto Montt', 'Iquique', 'Talca', 'Arica', 'Chillán', 'Osorno', 'Coquimbo', 'Calama', 'Copiapó', 'Valdivia', 'Curicó', 'Quilpué', 'San Bernardo', 'Puente Alto', 'Las Condes', 'Providencia', 'Ñuñoa', 'Maipú'];
type ToastData = {
  id: number;
  name: string;
  city: string;
  consultancy: Consultancy;
  minutesAgo: number;
};
function pickRandom<T>(arr: T[], avoid: T[] = []): T {
  const pool = arr.filter(x => !avoid.includes(x));
  const source = pool.length > 0 ? pool : arr;
  return source[Math.floor(Math.random() * source.length)];
}
function buildToast(recentNames: string[], idCounter: number): ToastData {
  const name = pickRandom(CHILEAN_NAMES, recentNames);
  const city = pickRandom(CHILEAN_CITIES);
  const consultancy = pickRandom(CONSULTANCIES);
  const minutesAgo = Math.floor(Math.random() * 14) + 2; // 2–15 min
  return {
    id: idCounter,
    name,
    city,
    consultancy,
    minutesAgo
  };
}
const VISIBLE_MS = 4000;
const FIRST_DELAY_MS = 3000;
const MIN_GAP_MS = 8000;
const MAX_GAP_MS = 15000;
export function PurchaseToast() {
  const [current, setCurrent] = useState<ToastData | null>(null);
  const prefersReducedMotion = useReducedMotion();
  const recentNamesRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const hideTimerRef = useRef<number | null>(null);
  const nextTimerRef = useRef<number | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    const scheduleNext = (delay: number) => {
      if (nextTimerRef.current) window.clearTimeout(nextTimerRef.current);
      nextTimerRef.current = window.setTimeout(showNext, delay);
    };
    const showNext = () => {
      if (!mountedRef.current) return;
      idRef.current += 1;
      const toast = buildToast(recentNamesRef.current, idRef.current);
      // Update rolling history of last 10 names
      const history = [...recentNamesRef.current, toast.name];
      if (history.length > 10) history.shift();
      recentNamesRef.current = history;
      setCurrent(toast);
      // Hide after VISIBLE_MS, then schedule the next one
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = window.setTimeout(() => {
        if (!mountedRef.current) return;
        setCurrent(null);
        const gap = Math.floor(Math.random() * (MAX_GAP_MS - MIN_GAP_MS + 1)) + MIN_GAP_MS;
        scheduleNext(gap);
      }, VISIBLE_MS);
    };
    scheduleNext(FIRST_DELAY_MS);
    return () => {
      mountedRef.current = false;
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
      if (nextTimerRef.current) window.clearTimeout(nextTimerRef.current);
    };
  }, []);
  const enter = prefersReducedMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 1,
    y: 0
  };
  const initial = prefersReducedMotion ? {
    opacity: 0,
    y: 0
  } : {
    opacity: 0,
    y: 24
  };
  const exit = prefersReducedMotion ? {
    opacity: 0,
    y: 0
  } : {
    opacity: 0,
    y: 24
  };
  return <div aria-live="polite" aria-atomic="true" className="pointer-events-none fixed inset-x-0 bottom-4 sm:bottom-6 z-[90] flex justify-center px-3" data-id="element-258">
      <AnimatePresence mode="wait" data-id="element-259">
        {current && <motion.div key={current.id} initial={initial} animate={enter} exit={exit} transition={{
        duration: prefersReducedMotion ? 0.01 : 0.45,
        ease: [0.22, 1, 0.36, 1]
      }} role="status" className="pointer-events-auto w-full max-w-sm sm:max-w-md" data-id="element-260">
            <div className="flex items-center gap-3 bg-brand-card/95 backdrop-blur-md border border-white/10 rounded-2xl shadow-2xl shadow-black/40 px-3.5 py-3 sm:px-4 sm:py-3.5" data-id="element-261">
              {/* Thumbnail */}
              <div className="relative shrink-0" data-id="element-262">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-brand-accent/30 to-brand-accent/5 border border-brand-accent/30 flex items-center justify-center text-2xl" data-id="element-263">
                  <span aria-hidden="true" data-id="element-264">{current.consultancy.emoji}</span>
                </div>
                <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-brand-dark border-2 border-brand-card flex items-center justify-center" data-id="element-265">
                  <CheckCircle2 className="w-3.5 h-3.5 text-brand-accent" data-id="element-266" />
                </span>
              </div>

              {/* Copy */}
              <div className="min-w-0 flex-1" data-id="element-267">
                <p className="text-[13px] sm:text-sm text-slate-200 leading-snug" data-id="element-268">
                  <span className="font-semibold text-white" data-id="element-269">
                    {current.name}
                  </span>{' '}
                  <span className="text-slate-400" data-id="element-270">en</span>{' '}
                  <span className="font-medium text-slate-100" data-id="element-271">
                    {current.city}
                  </span>
                  <br className="sm:hidden" data-id="element-272" />
                  <span className="text-slate-400" data-id="element-273"> acaba de agendar </span>
                  <span className="font-medium text-brand-accent" data-id="element-274">
                    {current.consultancy.title}
                  </span>
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5" data-id="element-275">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-276" />
                  hace {current.minutesAgo} min ·{' '}
                  {current.consultancy.editionLabel}
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}